import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError, apiFetch } from '../../lib/api';
import { getCurrentWarehouseId } from '../../lib/warehouse';
import { EXPORT_DATASETS, type ExportDataset, type ExportJobListItem } from './types';

function ErrorLine({ error }: { readonly error: unknown }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="field-error">
      {t(`errors:${error.code}`, { defaultValue: error.code, ...error.params })}
    </p>
  );
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]!) : undefined;
}

async function downloadViaPost(path: string, body: unknown, fallbackFilename: string): Promise<{ jobId?: string }> {
  const csrf = readCookie('wms_csrf');
  const warehouseId = getCurrentWarehouseId();
  const res = await fetch(`/api/v1${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(csrf ? { 'x-csrf-token': csrf } : {}),
      ...(warehouseId ? { 'x-warehouse-id': warehouseId } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    throw new ApiError(json, res.status);
  }
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return res.json();
  }
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return {};
}

async function downloadViaGet(path: string, fallbackFilename: string): Promise<void> {
  const warehouseId = getCurrentWarehouseId();
  const res = await fetch(`/api/v1${path}`, {
    credentials: 'include',
    headers: warehouseId ? { 'x-warehouse-id': warehouseId } : {},
  });
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    throw new ApiError(json, res.status);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const WAREHOUSE_SCOPED = new Set<ExportDataset>([
  'CURRENT_INVENTORY',
  'EXPIRING',
  'EXPIRED',
  'MOVEMENTS',
  'COUNT_VARIANCES',
]);

/** 导出中心（S14，§34）。每个数据集：筛选 → 格式 → 生成（≤2000 行直接下载，> 2000 行落 export_job）。 */
export function ExportCenterPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [dataset, setDataset] = useState<ExportDataset>('CURRENT_INVENTORY');
  const [format, setFormat] = useState<'XLSX' | 'CSV'>('XLSX');
  const [withinDays, setWithinDays] = useState('30');
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const { data: jobsData } = useQuery({
    queryKey: ['exports', 'list'],
    queryFn: () => apiFetch<{ items: ExportJobListItem[] }>('/exports'),
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const filters = dataset === 'EXPIRING' ? { withinDays: Number(withinDays) } : {};
      const result = await downloadViaPost(
        '/exports/generate',
        { dataset, format, filters },
        `${dataset.toLowerCase()}.${format.toLowerCase()}`,
      );
      return result;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['exports', 'list'] }),
  });

  const downloadMutation = useMutation({
    mutationFn: (jobId: string) => downloadViaGet(`/exports/${jobId}/download`, 'export'),
  });

  return (
    <div style={{ maxWidth: '720px' }}>
      <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('nav.reports')}</h1>

      <div className="card" style={{ padding: 'var(--space-4)', marginTop: 'var(--space-3)' }}>
        <label className="field">
          <span>{t('importExport.selectDataset')}</span>
          <select value={dataset} onChange={(e) => setDataset(e.target.value as ExportDataset)}>
            {EXPORT_DATASETS.map((d) => (
              <option key={d} value={d}>
                {t(`importExport.dataset${d}`)}
              </option>
            ))}
          </select>
        </label>
        {WAREHOUSE_SCOPED.has(dataset) || (
          <p className="field-hint">{t('importExport.companyWideDatasetHint')}</p>
        )}
        {dataset === 'EXPIRING' && (
          <label className="field">
            <span>{t('importExport.withinDaysLabel')}</span>
            <input
              type="number"
              min={1}
              value={withinDays}
              onChange={(e) => setWithinDays(e.target.value)}
              style={{ maxWidth: '120px' }}
            />
          </label>
        )}
        <label className="field">
          <span>{t('importExport.formatLabel')}</span>
          <select value={format} onChange={(e) => setFormat(e.target.value as 'XLSX' | 'CSV')}>
            <option value="XLSX">XLSX</option>
            <option value="CSV">CSV</option>
          </select>
        </label>

        <ErrorLine error={generateMutation.error} />
        {generateMutation.isSuccess && generateMutation.data?.jobId && (
          <p className="field-hint">{t('importExport.largeExportQueued')}</p>
        )}
        <button
          type="button"
          className="btn btn--primary"
          disabled={generateMutation.isPending}
          onClick={() => generateMutation.mutate()}
        >
          {generateMutation.isPending ? t('importExport.generating') : t('importExport.generateButton')}
        </button>
      </div>

      <h2 style={{ marginTop: 'var(--space-5)' }}>{t('importExport.recentExports')}</h2>
      {(jobsData?.items.length ?? 0) === 0 ? (
        <p className="field-hint">{t('importExport.emptyExports')}</p>
      ) : (
        <div className="table" style={{ display: 'block' }}>
          {jobsData!.items.map((job) => {
            const expired = job.expiresAt ? new Date(job.expiresAt).getTime() < Date.now() : false;
            return (
              <div key={job.id} className="tree__row" style={{ padding: 'var(--space-2) 0' }}>
                <span style={{ width: '180px' }}>{t(`importExport.dataset${job.dataset}`)}</span>
                <span style={{ width: '100px' }}>{job.format}</span>
                <span style={{ width: '120px' }}>{job.status}</span>
                <span style={{ width: '100px' }}>{job.rowCount ?? '—'}</span>
                <span style={{ width: '180px' }}>{fmt.format(new Date(job.createdAt))}</span>
                <span>
                  {job.status === 'DONE' && !expired && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={downloadMutation.isPending}
                      onClick={() => downloadMutation.mutate(job.id)}
                    >
                      {t('importExport.downloadButton')}
                    </button>
                  )}
                  {expired && <span className="field-hint">{t('importExport.linkExpired')}</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
      <ErrorLine error={downloadMutation.error} />
    </div>
  );
}
